import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import i18n from '../../i18n';

import { isSignupDemo, getDemoFailure } from '../../utils/signupExperiment';
import { realApi, createDemoApi } from '../../services/onboardingV2';
import { markSubcategoriesVideoSeen } from '../../constants/subcategoriesFeatureVideo';
import { createOnboardingAnalytics, getSignupAcquisition } from './onboardingV2.analytics';
import { useSignupFormTracking } from './useSignupFormTracking';
import { SignupAnalyticsContext } from './SignupAnalyticsContext';
import {
    useSignupDraft,
    useOnboardingState,
    useProductDraft,
    clearDemoState,
} from './useOnboardingDraft';

import AccountStep from './steps/AccountStep';
import StoreStep from './steps/StoreStep';
import NicheStep from './steps/NicheStep';
import CategoryStep from './steps/CategoryStep';
import ProductStep from './steps/ProductStep';
import SuccessStep from './steps/SuccessStep';
import WelcomeStep from './steps/WelcomeStep';
import WhatsAppStep from './steps/WhatsAppStep';
import WholesaleStep from './steps/WholesaleStep';
import StoreLivePreview from './components/StoreLivePreview/StoreLivePreview';
import { getStoreCategory } from './onboardingV2.categories';
import { firstWholesaleLevel, isWholesaleType } from './onboardingV2.wholesale';
import {
    SIGNUP_LANGUAGES,
    DEFAULT_LANGUAGE,
    detectCountry,
    detectLanguage,
    currencyForCountry,
    formatLocale,
    getCurrencyOptions,
} from './onboardingV2.locale';

/** Final slugs (04-spec §2.8). `step_name` in analytics = slug. */
export const STEPS = ['cuenta', 'nicho', 'tienda', 'categorias', 'producto', 'precios-mayor', 'whatsapp', 'lista'];
/** Only wholesale / "both" stores see this step (optional, can be skipped). */
const WHOLESALE_STEP = 'precios-mayor';
const SIGNUP_STEPS = ['inicio', 'cuenta', 'nicho', 'tienda', 'categorias', 'producto', 'precios-mayor', 'whatsapp'];
/** Old slugs (saved links, browser history) → current step. */
const LEGACY_SLUGS = { 'que-vendes': 'categorias' };
const DEMO_STEPS = [...SIGNUP_STEPS, 'lista'];
const ONBOARDING_STEPS = ['lista'];
const STEP_COMPONENTS = {
    inicio: WelcomeStep,
    cuenta: AccountStep,
    nicho: NicheStep,
    categorias: CategoryStep,
    tienda: StoreStep,
    producto: ProductStep,
    'precios-mayor': WholesaleStep,
    whatsapp: WhatsAppStep,
    lista: SuccessStep,
};

export const DEMO_STORE_ID = 0;

/**
 * The store's subdomain as the API builds it (`transformStoreURL`): only ASCII letters and digits,
 * lowercased ('Café Luna 2' → 'cafluna2'). Empty when nothing is left; the API then uses
 * `tienda` + part of the store id, which isn't known before signup.
 */
export const storeSubdomain = (text) =>
    String(text || '')
        .replace(/[^a-zA-Z0-9]+/g, '')
        .toLowerCase();

/** 'Moda Laura' → 'moda-laura' (only used for the fake demo store). */
export const slugify = (text) =>
    String(text || '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

const getOnboardingSlug = (pathname) => {
    const match = pathname.match(/^\/onboarding\/?([^/?#]*)/);
    return match ? match[1] : '';
};

/**
 * Signup v2 orchestrator. Renders the current step and owns the shared state.
 * - mode `signup` (AuthContainer, `/sign-up?signup=v2&step=…`): steps 1–2, or all 5 in demo.
 * - mode `onboarding` (OnboardingContainer, `/onboarding/:step`): steps 3–5 with a session.
 */
const SignUpV2Flow = ({ mode = 'signup' }) => {
    const { t } = useTranslation();
    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();
    const authStoreId = useSelector((state) => state.auth.storeId);
    const storeData = useSelector((state) => state.common.storeData);

    const isOnboarding = mode === 'onboarding';
    const isDemo = !isOnboarding && isSignupDemo(location.search);
    const failure = isDemo ? getDemoFailure(location.search) : null;
    const storeId = isDemo ? DEMO_STORE_ID : authStoreId;

    const requestedSlug = isOnboarding
        ? getOnboardingSlug(location.pathname)
        : new URLSearchParams(location.search).get('step') || 'inicio';

    // Demo `&reset=1`: start from a clean demo before the state hooks read the storage.
    const resetRequested = isDemo && new URLSearchParams(location.search).get('reset') === '1';
    const bootResetDone = useRef(false);
    if (resetRequested && !bootResetDone.current) {
        bootResetDone.current = true;
        clearDemoState();
    }

    const [draft, updateDraft, clearDraft] = useSignupDraft(isDemo);
    const [onboarding, updateOnboarding, clearOnboarding] = useOnboardingState(storeId, isDemo);
    const [productDraft, updateProductDraft, clearProductDraft] = useProductDraft(isDemo);

    // Language from the browser settings (`?lang=en|es|pt` overrides it). Only the onboarding is
    // translated for now, so the panel goes back to Spanish when the flow unmounts.
    const langParam = new URLSearchParams(location.search).get('lang');
    // After the account exists the signup draft is cleared: the store's onboarding state keeps it.
    const language = SIGNUP_LANGUAGES.includes(langParam)
        ? langParam
        : draft.language || onboarding.language || detectLanguage();
    // Declared first so it reads the page's own `lang` before the next effect changes it.
    useEffect(() => {
        const previousHtmlLang = document.documentElement.lang;
        return () => {
            i18n.changeLanguage(DEFAULT_LANGUAGE);
            document.documentElement.lang = previousHtmlLang;
        };
    }, []);
    useEffect(() => {
        if (i18n.language !== language) i18n.changeLanguage(language);
        document.documentElement.lang = language;
        if (draft.language !== language) updateDraft({ language });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [language]);

    // Country (WhatsApp prefix) and store currency from the browser's time zone / languages, no
    // IP lookup. The merchant can change both; the currency is saved on the store at the end.
    const country = useMemo(() => draft.isoCode || detectCountry(), [draft.isoCode]);
    const currency = onboarding.currency || currencyForCountry(country);
    const moneyLocale = formatLocale(language, country);
    const currencyOptions = useMemo(() => getCurrencyOptions(language), [language]);
    const setCurrency = useCallback((code) => updateOnboarding({ currency: code }), [updateOnboarding]);

    const onboardingRef = useRef(onboarding);
    onboardingRef.current = onboarding;

    // In memory only: the password is never persisted.
    const passwordRef = useRef('');
    const [emailInUse, setEmailInUse] = useState(false);
    const [passwordMissing, setPasswordMissing] = useState(false);
    const [productPreviewUrl, setProductPreviewUrl] = useState(null);
    // Live preview media. Object URLs feed the preview (not persisted); the File objects are kept so
    // the logo and category photos can be uploaded after the account exists.
    // { logo, logoName, logoFile, banner, bannerName, bannerFile,
    //   productPhotos: [{id, file, previewUrl, name}] (product being edited),
    //   savedProductPhotos: {productId: [photo]} (products already added),
    //   categoryPhotos: {id:url}, categoryFiles: {id:File} }. Kept here so photos survive "Volver".
    const [media, setMedia] = useState({
        logo: null,
        logoName: null,
        logoFile: null,
        banner: null,
        bannerName: null,
        bannerFile: null,
        productPhotos: [],
        savedProductPhotos: {},
        categoryPhotos: {},
        categoryFiles: {},
    });

    const setLogoPhoto = useCallback((file) => {
        setMedia((prev) => {
            if (prev.logo) URL.revokeObjectURL(prev.logo);
            if (!file) return { ...prev, logo: null, logoName: null, logoFile: null };
            return { ...prev, logo: URL.createObjectURL(file), logoName: file.name, logoFile: file };
        });
    }, []);

    const setBannerPhoto = useCallback((file) => {
        setMedia((prev) => {
            if (prev.banner) URL.revokeObjectURL(prev.banner);
            if (!file) return { ...prev, banner: null, bannerName: null, bannerFile: null };
            return { ...prev, banner: URL.createObjectURL(file), bannerName: file.name, bannerFile: file };
        });
    }, []);

    /** Adds already-normalized product photos (the first one is the main photo). */
    const addProductPhotos = useCallback((files) => {
        const added = (files || []).filter(Boolean).map((file, i) => ({
            id: `photo-${Date.now()}-${i}`,
            file,
            previewUrl: URL.createObjectURL(file),
            name: file.name,
        }));
        if (added.length) setMedia((prev) => ({ ...prev, productPhotos: [...prev.productPhotos, ...added] }));
    }, []);

    const removeProductPhoto = useCallback((id) => {
        setMedia((prev) => {
            const gone = prev.productPhotos.find((p) => p.id === id);
            if (gone) URL.revokeObjectURL(gone.previewUrl);
            return { ...prev, productPhotos: prev.productPhotos.filter((p) => p.id !== id) };
        });
    }, []);

    // Products already added in the producto step (no limit). Name / price / category persist in
    // the onboarding state; their photos live in `media.savedProductPhotos` (memory only).
    const savedProducts = Array.isArray(onboarding.products) ? onboarding.products : [];

    /** Moves the product being edited (draft + photos) to the list and clears the form. */
    const saveProduct = useCallback(
        ({ name, price, categoryId }) => {
            const id = `product-${Date.now()}`;
            const product = { id, name: String(name).trim(), price: Number(price), categoryId: categoryId || null };
            updateOnboarding({ products: [...(onboardingRef.current.products || []), product] });
            setMedia((prev) => ({
                ...prev,
                productPhotos: [],
                savedProductPhotos: { ...prev.savedProductPhotos, [id]: prev.productPhotos },
            }));
            updateProductDraft({ name: '', price: '', categoryId: categoryId || null });
            return product;
        },
        [updateOnboarding, updateProductDraft]
    );

    const removeProduct = useCallback(
        (id) => {
            updateOnboarding({ products: (onboardingRef.current.products || []).filter((p) => p.id !== id) });
            setMedia((prev) => {
                (prev.savedProductPhotos[id] || []).forEach((photo) => URL.revokeObjectURL(photo.previewUrl));
                const next = { ...prev.savedProductPhotos };
                delete next[id];
                return { ...prev, savedProductPhotos: next };
            });
        },
        [updateOnboarding]
    );

    /** Puts a saved product back in the form (taken out of the list until it's saved again). */
    const editProduct = useCallback(
        (id) => {
            const product = (onboardingRef.current.products || []).find((p) => p.id === id);
            if (!product) return;
            updateOnboarding({ products: (onboardingRef.current.products || []).filter((p) => p.id !== id) });
            setMedia((prev) => {
                const next = { ...prev.savedProductPhotos };
                const photos = next[id] || [];
                delete next[id];
                return { ...prev, productPhotos: [...photos, ...prev.productPhotos], savedProductPhotos: next };
            });
            updateProductDraft({ name: product.name, price: product.price, categoryId: product.categoryId || null });
        },
        [updateOnboarding, updateProductDraft]
    );

    const setCategoryPhoto = useCallback((id, file) => {
        setMedia((prev) => {
            const next = { ...prev.categoryPhotos };
            const nextFiles = { ...prev.categoryFiles };
            if (next[id]) URL.revokeObjectURL(next[id]);
            if (!file) {
                delete next[id];
                delete nextFiles[id];
            } else {
                next[id] = URL.createObjectURL(file);
                nextFiles[id] = file;
            }
            return { ...prev, categoryPhotos: next, categoryFiles: nextFiles };
        });
    }, []);
    const api = useMemo(
        () =>
            isDemo
                ? createDemoApi({
                      failure,
                      getDemoStore: () => {
                          const current = onboardingRef.current;
                          const name = current.storeName || t('SignUpV2.demo.storeName');
                          return {
                              id: DEMO_STORE_ID,
                              name,
                              url: slugify(name) || 'mi-tienda-demo',
                              domain: null,
                              active: true,
                              default_user_store: { mobile_phone_number: current.phone || '0' },
                          };
                      },
                  })
                : realApi,
        [isDemo, failure, t]
    );
    const analytics = useMemo(() => createOnboardingAnalytics(isDemo), [isDemo]);

    // --- Navigation -----------------------------------------------------------------------
    const goTo = useCallback(
        (slug, { replace = false } = {}) => {
            let target;
            if (isOnboarding) {
                target = `/onboarding/${slug}`;
            } else {
                const params = new URLSearchParams(location.search);
                params.set('signup', 'v2');
                params.set('step', slug);
                target = `/sign-up?${params.toString()}`;
            }
            if (replace) history.replace(target);
            else history.push(target);
        },
        [history, isOnboarding, location.search]
    );

    // Retail: 6 steps. Wholesale / both: 7 (adds "precios-mayor" after the product).
    const isWholesaleFlow = isWholesaleType(draft.storeType);
    const flowSteps = isWholesaleFlow ? STEPS : STEPS.filter((s) => s !== WHOLESALE_STEP);
    const totalSteps = flowSteps.length;
    const stepNumberOf = (stepSlug) => flowSteps.indexOf(stepSlug) + 1;

    // --- Route guards ---------------------------------------------------------------------
    const allowed = isOnboarding ? ONBOARDING_STEPS : isDemo ? DEMO_STEPS : SIGNUP_STEPS;
    let redirectTo = null;
    if (resetRequested) {
        redirectTo = requestedSlug;
    } else if (LEGACY_SLUGS[requestedSlug]) {
        redirectTo = LEGACY_SLUGS[requestedSlug];
    } else if (!allowed.includes(requestedSlug)) {
        // Onboarding mode only has the success step now (everything else happens before login).
        redirectTo = isOnboarding ? 'lista' : 'cuenta';
    } else if (requestedSlug === WHOLESALE_STEP && !isWholesaleFlow) {
        // Retail stores don't have volume pricing in the onboarding.
        redirectTo = 'whatsapp';
    } else if (isDemo) {
        // Demo: any step can be opened by URL (review deep links). It never sends the password,
        // and ProductStep reuses an existing productId, so the guards below are not needed.
    } else if (requestedSlug === 'whatsapp' && !passwordRef.current) {
        // Account is created at the last step; the password only lives in memory, ask it again.
        redirectTo = 'cuenta';
    }
    const exitToPanel = isOnboarding && !!onboarding.completedAt && requestedSlug !== 'lista';

    useEffect(() => {
        if (exitToPanel) {
            history.replace('/');
            return;
        }
        if (resetRequested) {
            // Also clears the in-memory values when the flow was already mounted.
            clearDemoState();
            clearDraft();
            clearOnboarding();
            clearProductDraft();
            passwordRef.current = '';
            setProductPreviewUrl(null);
            const params = new URLSearchParams(location.search);
            params.delete('reset');
            history.replace(`/sign-up?${params.toString()}`);
            return;
        }
        if (redirectTo) {
            if (requestedSlug === 'tienda' && redirectTo === 'cuenta') setPasswordMissing(true);
            goTo(redirectTo, { replace: true });
        }
    }, [
        exitToPanel,
        redirectTo,
        requestedSlug,
        resetRequested,
        goTo,
        history,
        location.search,
        clearDraft,
        clearOnboarding,
        clearProductDraft,
    ]);

    // --- Analytics ------------------------------------------------------------------------
    const slug = redirectTo || exitToPanel ? null : requestedSlug;
    const stepIndex = STEPS.indexOf(slug) + 1;
    const stepViewedAt = useRef(Date.now());
    const startedRef = useRef(false);
    const previousSlugRef = useRef(null);
    // Set by the steps' "Volver" (`noteBack`) to tell it apart from the browser's back and redirects.
    const backIntentRef = useRef(false);
    const noteBack = useCallback(() => {
        backIntentRef.current = true;
    }, []);

    useEffect(() => {
        analytics.register();
    }, [analytics]);

    // store_type and language live only in Mixpanel (the backend doesn't store them): every later
    // event carries them.
    useEffect(() => {
        if (draft.storeType) analytics.register({ store_type: draft.storeType });
    }, [analytics, draft.storeType]);
    useEffect(() => {
        analytics.register({ language });
    }, [analytics, language]);

    useEffect(() => {
        if (!slug) return;
        stepViewedAt.current = Date.now();
        if (slug === 'cuenta' && !startedRef.current) {
            startedRef.current = true;
            if (!draft.startedAt) updateDraft({ startedAt: Date.now() });
            // Same shape as v1 (source, referrer, landing path, device, UTMs) for the A/B comparison.
            const acquisition = getSignupAcquisition();
            // The backend doesn't keep any of this (`source` is ignored by POST /users): kept in the
            // draft so the Mixpanel profile gets it when the account is created.
            if (!draft.acquisition) updateDraft({ acquisition });
            analytics.track('signup_started', acquisition);
        }
        // Every move to an earlier step: the step's "Volver" (button), the browser / phone back
        // (browser) or a redirect (e.g. email in use sends the visitor back to the account step).
        const previous = previousSlugRef.current;
        previousSlugRef.current = slug;
        if (previous && STEPS.indexOf(slug) < STEPS.indexOf(previous)) {
            analytics.track('signup_step_back', {
                from_step: previous,
                to_step: slug,
                via: history.action === 'POP' ? 'browser' : backIntentRef.current ? 'button' : 'redirect',
            });
        }
        backIntentRef.current = false;
        analytics.track('signup_step_viewed', {
            step_name: slug,
            step_index: STEPS.indexOf(slug) + 1,
        });
        // Once per step view.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug, analytics]);

    const trackStep = useCallback(
        (event, extra = {}) => {
            analytics.track(event, {
                step_name: slug,
                step_index: STEPS.indexOf(slug) + 1,
                ...(event === 'signup_step_completed'
                    ? { duration_ms: Date.now() - stepViewedAt.current }
                    : {}),
                ...extra,
            });
        },
        [analytics, slug]
    );

    // Fields filled, tab hidden / closed and comeback on each step (drop-off inside the step).
    useSignupFormTracking({
        track: analytics.track,
        stepName: slug,
        stepIndex: STEPS.indexOf(slug) + 1,
        enabled: !!slug,
    });
    const analyticsContext = useMemo(() => ({ analytics, stepName: slug }), [analytics, slug]);

    // --- Exit -----------------------------------------------------------------------------
    /** Marks the onboarding as done (real mode) so the panel does not interrupt with popups. */
    const markCompleted = useCallback(() => {
        if (isDemo) return;
        if (!onboardingRef.current.completedAt) updateOnboarding({ completedAt: Date.now() });
        markSubcategoriesVideoSeen(storeId);
    }, [isDemo, storeId, updateOnboarding]);

    const resetDemo = useCallback(() => {
        clearDemoState();
        clearDraft();
        clearOnboarding();
        clearProductDraft();
        passwordRef.current = '';
        setProductPreviewUrl(null);
        history.push('/sign-up?signup=v2&demo=1&step=inicio');
    }, [clearDraft, clearOnboarding, clearProductDraft, history]);

    if (!slug) return null;

    // Desktop-only live preview of the storefront, fed by the form data as the user types.
    const previewCategories = (onboarding.categories || [])
        .map((entry) => (typeof entry === 'string' ? getStoreCategory(entry) : entry))
        .filter(Boolean)
        .map((category) => ({
            ...category,
            photoUrl: media.categoryPhotos[category.id] || category.photoUrl || null,
        }));
    // Volume pricing hint ("Desde 12 u. a $71.200"): live while editing the step, hidden if skipped.
    const showWholesaleLevel =
        isWholesaleFlow && (slug === WHOLESALE_STEP || !onboarding.wholesaleSkipped);
    const draftPhotoUrl = (media.productPhotos[0] && media.productPhotos[0].previewUrl) || productPreviewUrl;
    const previewProducts = [
        ...savedProducts.map((p) => ({
            name: p.name,
            price: p.price,
            photoUrl: (media.savedProductPhotos[p.id] || [])[0] ? media.savedProductPhotos[p.id][0].previewUrl : null,
        })),
        ...(productDraft.name || (productDraft.price !== '' && productDraft.price != null) || draftPhotoUrl
            ? [{ name: productDraft.name, price: productDraft.price, photoUrl: draftPhotoUrl }]
            : []),
    ].map((p) => {
        const level = showWholesaleLevel ? firstWholesaleLevel(onboarding.wholesaleTiers || [], p.price) : null;
        return { ...p, tierMinQty: level ? level.minQty : null, tierPrice: level ? level.unitPrice : null };
    });
    const preview = (
        <StoreLivePreview
            storeName={draft.store}
            displayUrl={draft.store ? `${storeSubdomain(draft.store) || 'tu-tienda'}.ecometri.shop` : ''}
            categories={previewCategories}
            logoUrl={media.logo}
            bannerUrl={media.banner}
            products={previewProducts}
            currency={currency}
            locale={moneyLocale}
            storeType={draft.storeType}
            isWholesale={isWholesaleFlow}
        />
    );

    const ctx = {
        t,
        mode,
        isDemo,
        failure,
        api,
        analytics,
        dispatch,
        history,
        storeId,
        storeData: isDemo ? {} : storeData || {},
        slug,
        stepIndex,
        totalSteps,
        stepNumberOf,
        isWholesaleFlow,
        nextAfterProduct: isWholesaleFlow ? WHOLESALE_STEP : 'whatsapp',
        language,
        country,
        currency,
        setCurrency,
        currencyOptions,
        moneyLocale,
        savedProducts,
        saveProduct,
        removeProduct,
        editProduct,
        goTo,
        noteBack,
        trackStep,
        draft,
        updateDraft,
        clearDraft,
        passwordRef,
        emailInUse,
        setEmailInUse,
        passwordMissing,
        setPasswordMissing,
        onboarding,
        updateOnboarding,
        productDraft,
        updateProductDraft,
        clearProductDraft,
        productPreviewUrl,
        setProductPreviewUrl,
        media,
        setLogoPhoto,
        setBannerPhoto,
        setCategoryPhoto,
        addProductPhotos,
        removeProductPhoto,
        markCompleted,
        resetDemo,
        preview,
    };

    const Step = STEP_COMPONENTS[slug];
    return (
        <SignupAnalyticsContext.Provider value={analyticsContext}>
            <Step ctx={ctx} />
        </SignupAnalyticsContext.Provider>
    );
};

/** Reads a cookie by name (v1's helper never strips the leading space). */
export const getCookie = (name) => {
    const prefix = `${name}=`;
    const found = document.cookie
        .split(';')
        .map((part) => part.trim())
        .find((part) => part.indexOf(prefix) === 0);
    if (!found) return '';
    const value = found.substring(prefix.length);
    try {
        return decodeURIComponent(value);
    } catch {
        return value;
    }
};

export default SignUpV2Flow;
